import { Controller } from "@hotwired/stimulus";
import {
  Editor,
  defaultValueCtx,
  rootCtx,
  editorViewCtx,
  serializerCtx,
} from "@milkdown/core";
import {
  commonmark,
  toggleStrongCommand,
  toggleEmphasisCommand,
  wrapInBulletListCommand,
  wrapInOrderedListCommand,
  toggleLinkCommand,
} from "@milkdown/preset-commonmark";
import { callCommand } from "@milkdown/utils";

const TOOLBAR_BUTTONS = [
  { icon: "fa fa-bold", title: "Fett (Bold)", command: toggleStrongCommand },
  {
    icon: "fa fa-italic",
    title: "Kursiv (Italic)",
    command: toggleEmphasisCommand,
  },
  { icon: "fa fa-list", title: "Aufzählung", command: wrapInBulletListCommand },
  {
    icon: "fa fa-list-check",
    title: "Nummerierte Liste",
    command: wrapInOrderedListCommand,
  },
  { icon: "fa fa-link", title: "Link einfügen", command: "link" },
  { icon: "fa fa-link-slash", title: "Link entfernen", command: "unlink" },
];

export default class extends Controller {
  async connect() {
    this.textarea = this.element.querySelector("textarea");
    if (!this.textarea) return;

    this._buildEditorDOM();

    this.milkdown = await Editor.make()
      .config((ctx) => {
        ctx.set(rootCtx, this.editorRoot);
        ctx.set(defaultValueCtx, this.textarea.value || "");
      })
      .use(commonmark)
      .create();

    this._wireFormSync();
  }

  disconnect() {
    const form = this.element.closest("form");
    if (form && this._syncHandler) {
      form.removeEventListener("submit", this._syncHandler);
    }
    this.milkdown?.destroy();
  }

  // private

  _buildEditorDOM() {
    this.wrapper = document.createElement("div");
    this.wrapper.classList.add("milkdown-wrapper");

    const toolbar = this._buildToolbar();
    this.editorRoot = document.createElement("div");
    this.editorRoot.classList.add("milkdown-root");

    this.wrapper.appendChild(toolbar);
    this.wrapper.appendChild(this.editorRoot);

    this.textarea.insertAdjacentElement("afterend", this.wrapper);
    this.textarea.style.display = "none";
  }

  _buildToolbar() {
    const toolbar = document.createElement("div");
    toolbar.classList.add("milkdown-toolbar");

    TOOLBAR_BUTTONS.forEach(({ icon, title, command }) => {
      const btn = document.createElement("button");
      btn.type = "button";
      btn.title = title;
      btn.classList.add("milkdown-toolbar-btn");
      const i = document.createElement("i");
      i.className = icon;
      btn.appendChild(i);
      // preventDefault on mousedown keeps editor focus and preserves text selection
      btn.addEventListener("mousedown", (e) => e.preventDefault());
      btn.addEventListener("click", () => this._execCommand(command));
      toolbar.appendChild(btn);
    });

    return toolbar;
  }

  _execCommand(command) {
    if (command === "link") {
      this._handleLink();
    } else if (command === "unlink") {
      this._handleUnlink();
    } else {
      this.milkdown?.action(callCommand(command.key));
    }
  }

  _handleLink() {
    const linkInfo = this.milkdown?.action((ctx) => {
      const { state } = ctx.get(editorViewCtx);
      const linkMarkType = state.schema.marks.link;
      const { $from } = state.selection;

      // Check cursor position (covers cursor inside a link with no selection)
      const mark =
        $from.marks().find((m) => m.type === linkMarkType) ||
        $from.nodeAfter?.marks.find((m) => m.type === linkMarkType);

      if (!mark) return null;

      // Find the full span of this link mark in the parent node
      const range = this._getMarkRange($from, linkMarkType);
      return range
        ? { href: mark.attrs.href, from: range.from, to: range.to }
        : null;
    });

    if (linkInfo) {
      const href = window.prompt(
        "URL bearbeiten (leer lassen zum Entfernen):",
        linkInfo.href,
      );
      if (href === null) return;
      if (href === "") {
        this._applyToRange(linkInfo.from, linkInfo.to, (state, tr) =>
          tr.removeMark(linkInfo.from, linkInfo.to, state.schema.marks.link),
        );
      } else {
        this._applyToRange(linkInfo.from, linkInfo.to, (state, tr) => {
          const newMark = state.schema.marks.link.create({ href });
          return tr
            .removeMark(linkInfo.from, linkInfo.to, state.schema.marks.link)
            .addMark(linkInfo.from, linkInfo.to, newMark);
        });
      }
    } else {
      const href = window.prompt("URL eingeben:");
      if (!href) return;
      this.milkdown?.action(callCommand(toggleLinkCommand.key, { href }));
    }
  }

  _handleUnlink() {
    this.milkdown?.action((ctx) => {
      const view = ctx.get(editorViewCtx);
      const { state, dispatch } = view;
      const linkMarkType = state.schema.marks.link;
      const { $from, from, to } = state.selection;

      // Cursor inside a link with no selection → expand to full link range
      if (from === to) {
        const mark =
          $from.marks().find((m) => m.type === linkMarkType) ||
          $from.nodeAfter?.marks.find((m) => m.type === linkMarkType);
        if (!mark) return;
        const range = this._getMarkRange($from, linkMarkType);
        if (range)
          dispatch(state.tr.removeMark(range.from, range.to, linkMarkType));
      } else {
        // Selection → remove all links within it
        dispatch(state.tr.removeMark(from, to, linkMarkType));
      }
    });
  }

  // Walk the parent's children to find the contiguous range covered by a mark
  _getMarkRange($pos, markType) {
    const parent = $pos.parent;
    const parentStart = $pos.start();
    const ranges = [];
    parent.forEach((child, offset) => {
      ranges.push({
        from: parentStart + offset,
        to: parentStart + offset + child.nodeSize,
        marks: child.marks,
      });
    });

    const cursorPos = $pos.pos;
    const idx = ranges.findIndex(
      (r) => r.from <= cursorPos && cursorPos <= r.to,
    );
    if (idx === -1) return null;

    const mark = ranges[idx].marks.find((m) => m.type === markType);
    if (!mark) return null;

    let from = ranges[idx].from;
    let to = ranges[idx].to;

    for (let i = idx - 1; i >= 0 && mark.isInSet(ranges[i].marks); i--)
      from = ranges[i].from;
    for (
      let j = idx + 1;
      j < ranges.length && mark.isInSet(ranges[j].marks);
      j++
    )
      to = ranges[j].to;

    return { from, to };
  }

  _applyToRange(from, to, fn) {
    this.milkdown?.action((ctx) => {
      const view = ctx.get(editorViewCtx);
      const { state, dispatch } = view;
      dispatch(fn(state, state.tr));
    });
  }

  _syncContent() {
    if (!this.milkdown) return;
    let content = this.milkdown.action((ctx) => {
      const view = ctx.get(editorViewCtx);
      const serializer = ctx.get(serializerCtx);
      return serializer(view.state.doc);
    });
    // Remove blank lines between list items to produce tight lists (avoids <p> wrapping)
    content = content.replace(
      /^([ \t]*[-*+\d].*)\n\n(?=[ \t]*[-*+\d])/gm,
      "$1\n",
    );
    this.textarea.value = content;
  }

  _wireFormSync() {
    const form = this.element.closest("form");
    if (!form) return;
    this._syncHandler = () => this._syncContent();
    form.addEventListener("submit", this._syncHandler);
  }
}
